import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/utils/css'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        primaryghost: 'text-primary border border-primary border-solid',
        custom: 'font-normal hover:bg-[#1c1f2308]',
        // primarygradient: 'text-[#fff] bg-gradient-to-r from-[#3D7BFF] to-[#004FF8]',
        primarygradient:
          'text-[#fff] bg-gradient-to-r from-[#004FF8] to-[#C17BFF]',
        text: 'hover:bg-transparent',
        gradient: 'text-white bg-gradient-to-r from-brand to-[#C17BFF]',
        solidGray: 'bg-content-second text-white hover:bg-content-second/90',
        solidBrand: 'bg-brand text-white hover:bg-brand/90',
        solidDanger: 'bg-danger text-white hover:bg-danger/90',
        lightGray: 'bg-fill-1 text-content-second hover:bg-fill-2',
        lightBrand: 'bg-brand/10 text-brand hover:bg-brand/15',
        lightDanger: 'bg-danger/10 text-danger hover:bg-danger/15',
        rainbow:
          "relative z-0 bg-transparent text-white hover:bg-transparent after:absolute after:inset-0 after:-z-10 after:bg-[#494C4F] after:rounded-[inherit] after:transition-colors hover:after:bg-[#494C4F]/90 after:content-[''] before:absolute before:left-1/2 before:-translate-x-1/2 before:bottom-0 before:w-[80%] before:h-[8px] before:-z-20 before:rounded-[999px] before:blur-[10px] before:bg-[linear-gradient(90deg,#FF4242_0%,#A0FF41_25%,#43A1FF_50%,#41D0FF_75%,#A042FF_100%)] before:content-['']",
      },

      size: {
        default: 'h-9 px-4 py-2',
        xs: 'h-6 px-2 py-1 text-xs',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
        iconsm: 'h-7 w-7',
        iconxs: 'h-6 w-6',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
